import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { Badge, Button, Card, Col, Form, Row, Spinner, Alert } from 'react-bootstrap';
import { validateCertificate } from './utils/certificateValidator';
import { useForm } from 'react-hook-form';

const TabEditar = ({
    rnc,
    guardando,
    onSave,
    onCancel,
    onGenerarCadena
}) => {
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm();
    const [certificadoFile, setCertificadoFile] = React.useState(null);
    const [certificadoNombre, setCertificadoNombre] = React.useState('');
    const [certificadoValidacion, setCertificadoValidacion] = React.useState({
        validado: false,
        esValido: false,
        mensaje: '',
        info: null,
        warnings: []
    });
    const [validandoCertificado, setValidandoCertificado] = React.useState(false);

    React.useEffect(() => {
        if (rnc) {
            setValue("idRepublicaDominicanaFacturacionExistencias", rnc.idRepublicaDominicanaFacturacionExistencias);
            setValue("vNombre", rnc.vNombre);
            setValue("vRNC", rnc.vRNC);
            setValue("vDireccionFiscal", rnc.vDireccionFiscal);
            setValue("vTelefono", rnc.vTelefono);
            setValue("vEmail", rnc.vEmail);
            setValue("vTipoContribuyente", rnc.vTipoContribuyente);
            setValue("vActividadEconomica", rnc.vActividadEconomica);
            setValue("vUsuario", rnc.vUsuario);
            setValue("vPassword", rnc.vPassword);
            setValue("iAmbiente", rnc.iAmbiente !== null && rnc.iAmbiente !== undefined ? String(rnc.iAmbiente) : '');
            setValue("vPasswordCertificado", rnc.vPasswordCertificado || '');
            setValue("bIsActive", rnc.bIsActive);
            setValue("idClienteAkasia", rnc.idClienteAkasia || '');

            if (rnc.vCertificadoDGII) {
                setCertificadoNombre('Certificado cargado previamente');
                // mantener el certificado en memoria para permitir validación en edición
                setCertificadoFile(rnc.vCertificadoDGII);
            }
        }
    }, [rnc, setValue]);

    const handleCertificadoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validar que sea un archivo .pfx o .p12
            const extension = file.name.toLowerCase().split('.').pop();
            if (extension !== 'pfx' && extension !== 'p12') {
                alert('Por favor seleccione un archivo de certificado válido (.pfx o .p12)');
                event.target.value = '';
                return;
            }

            // Convertir el archivo a base64
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64 = e.target.result.split(',')[1]; // Obtener solo la parte base64
                setCertificadoFile(base64);
                setCertificadoNombre(file.name);

                // Reset validación
                setCertificadoValidacion({
                    validado: false,
                    esValido: false,
                    mensaje: '',
                    info: null,
                    warnings: []
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleFormSubmit = (data) => {
        const values = {
            ...data,
            idClienteAkasia: rnc?.idClienteAkasia || null,
            vCertificadoDGII: certificadoFile || rnc?.vCertificadoDGII || null
        };
        onSave(values);
    };

    // watch password field to trigger validation
    const passwordValue = watch('vPasswordCertificado');

    // validar certificado cuando haya archivo/base64 y contraseña
    React.useEffect(() => {
        if (!certificadoFile || !passwordValue) {
            // resetear estado si falta info
            setCertificadoValidacion({
                validado: false,
                esValido: false,
                mensaje: '',
                info: null,
                warnings: []
            });
            return;
        }

        let mounted = true;
        setValidandoCertificado(true);

        const timer = setTimeout(() => {
            try {
                const resultado = validateCertificate(certificadoFile, passwordValue);

                if (!mounted) return;

                setCertificadoValidacion({
                    validado: true,
                    esValido: resultado.isValid,
                    mensaje: resultado.error || 'Certificado válido',
                    info: resultado.certificateInfo,
                    warnings: resultado.warnings || []
                });
            } catch (error) {
                console.error('Error validando certificado:', error);
                if (!mounted) return;
                setCertificadoValidacion({
                    validado: true,
                    esValido: false,
                    mensaje: 'Error al validar el certificado',
                    info: null,
                    warnings: []
                });
            } finally {
                if (mounted) setValidandoCertificado(false);
            }
        }, 300);

        return () => {
            mounted = false;
            clearTimeout(timer);
        };
    }, [certificadoFile, passwordValue]);

    if (!rnc) return null;


    return (
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
            <Form.Control
                type="hidden"
                {...register("idRepublicaDominicanaFacturacionExistencias")}
            />
            <Form.Control
                type="hidden"
                {...register("idClienteAkasia")}
            />

            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">Estado</Form.Label>
                <div className="d-flex align-items-center">
                    <Form.Check
                        type="switch"
                        id="status-switch"
                        {...register("bIsActive")}
                        defaultChecked={rnc.bIsActive}
                        className="me-3"
                    />
                    <Badge bg={rnc.bIsActive ? 'success' : 'danger'}>
                        {rnc.bIsActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                </div>
            </Form.Group>

            <hr className="my-3" />

            {/* Sección de Cliente - Solo Informativa */}
            {(rnc?.idClienteAkasia || rnc?.nombreLicenciatario) && (
                <>
                    <Card className="mb-4 border-info">
                        <Card.Header className="bg-light">
                            <h6 className="mb-0 fw-semibold small">
                                <FontAwesomeIcon icon="user" className="me-2 text-info" />
                                Cliente Asociado (Solo Lectura)
                            </h6>
                        </Card.Header>
                        <Card.Body className="bg-light">
                            {rnc.idClienteAkasia && (
                                <div className="mb-2">
                                    <label className="text-muted small mb-1">ID Cliente Akasia</label>
                                    <div className="fw-semibold">
                                        <code className="bg-white px-2 py-1 rounded">#{rnc.idClienteAkasia}</code>
                                    </div>
                                </div>
                            )}

                            {rnc.nombreLicenciatario && (
                                <div className="mb-2">
                                    <label className="text-muted small mb-1">Nombre del Licenciatario</label>
                                    <div className="fw-semibold">{rnc.nombreLicenciatario}</div>
                                </div>
                            )}

                            <div className="alert alert-info small mb-0 mt-3">
                                <FontAwesomeIcon icon="info-circle" className="me-2" />
                                La información del cliente no puede modificarse desde esta pantalla
                            </div>
                        </Card.Body>
                    </Card>
                    <hr className="my-3" />
                </>
            )}

            <h6 className="mb-3 fw-semibold small">
                <FontAwesomeIcon icon="building" className="me-2 text-primary" />
                Información del Contribuyente
            </h6>

            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">RNC</Form.Label>
                <Form.Control
                    size="sm"
                    type="text"
                    disabled
                    {...register("vRNC")}
                    className="bg-light"
                />
                <Form.Text className="text-muted">
                    El RNC no puede ser modificado
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">Razón Social / Nombre Comercial</Form.Label>
                <Form.Control
                    size="sm"
                    type="text"
                    isInvalid={!!errors.vNombre}
                    {...register("vNombre", {
                        required: 'El nombre es requerido',
                        maxLength: 200
                    })}
                    onKeyUp={(e) => { e.target.value = e.target.value.toUpperCase(); }}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.vNombre && errors.vNombre.message}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">Dirección Fiscal</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    isInvalid={!!errors.vDireccionFiscal}
                    {...register("vDireccionFiscal", {
                        maxLength: 500
                    })}
                    onKeyUp={(e) => { e.target.value = e.target.value.toUpperCase(); }}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.vDireccionFiscal && errors.vDireccionFiscal.message}
                </Form.Control.Feedback>
            </Form.Group>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Teléfono</Form.Label>
                        <Form.Control
                            size="sm"
                            type="text"
                            isInvalid={!!errors.vTelefono}
                            {...register("vTelefono", {
                                maxLength: 20
                            })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vTelefono && errors.vTelefono.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Email</Form.Label>
                        <Form.Control
                            size="sm"
                            type="email"
                            isInvalid={!!errors.vEmail}
                            {...register("vEmail", {
                                maxLength: 100,
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Email inválido'
                                }
                            })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vEmail && errors.vEmail.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Tipo de Contribuyente</Form.Label>
                        <Form.Select
                            size="sm"
                            isInvalid={!!errors.vTipoContribuyente}
                            {...register("vTipoContribuyente")}
                        >
                            <option value="">Seleccione...</option>
                            <option value="PERSONA_FISICA">Persona Física</option>
                            <option value="PERSONA_JURIDICA">Persona Jurídica</option>
                            <option value="REGIMEN_SIMPLIFICADO">Régimen Simplificado</option>
                            <option value="GRAN_CONTRIBUYENTE">Gran Contribuyente</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.vTipoContribuyente && errors.vTipoContribuyente.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Actividad Económica</Form.Label>
                        <Form.Control
                            size="sm"
                            type="text"
                            isInvalid={!!errors.vActividadEconomica}
                            {...register("vActividadEconomica", {
                                maxLength: 200
                            })}
                            onKeyUp={(e) => { e.target.value = e.target.value.toUpperCase(); }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vActividadEconomica && errors.vActividadEconomica.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <hr className="my-3" />

            <h6 className="mb-3 fw-semibold small">
                <FontAwesomeIcon icon="cog" className="me-2 text-primary" />
                Configuración de Ambiente
            </h6>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Ambiente</Form.Label>
                        <Form.Select
                            size="sm"
                            isInvalid={!!errors.iAmbiente}
                            {...register("iAmbiente", {
                                required: 'El ambiente es requerido'
                            })}
                        >
                            <option value="">Seleccione...</option>
                            <option value="0">Desarrollo</option>
                            <option value="1">Test</option>
                            <option value="2">Certificación</option>
                            <option value="3">Producción</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.iAmbiente && errors.iAmbiente.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <hr className="my-3" />

            <h6 className="mb-3 fw-semibold small">
                <FontAwesomeIcon icon="certificate" className="me-2 text-primary" />
                Certificado Digital
            </h6>

            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">Archivo del Certificado (.pfx / .p12)</Form.Label>
                <Form.Control
                    size="sm"
                    type="file"
                    accept=".pfx,.p12"
                    onChange={handleCertificadoChange}
                />
                <Form.Text className="text-muted">
                    {certificadoNombre ? (
                        <>
                            <FontAwesomeIcon icon="check-circle" className="text-success me-1" />
                            {certificadoNombre}
                        </>
                    ) : (
                        'Seleccione un archivo de certificado digital (.pfx o .p12)'
                    )}
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">Contraseña del Certificado</Form.Label>
                <Form.Control
                    size="sm"
                    type="password"
                    isInvalid={!!errors.vPasswordCertificado || (certificadoValidacion.validado && !certificadoValidacion.esValido && certificadoFile)}
                    isValid={certificadoValidacion.validado && certificadoValidacion.esValido && certificadoFile}
                    {...register("vPasswordCertificado", {
                        maxLength: 100
                    })}
                    placeholder="Contraseña para desbloquear el certificado"
                    disabled={!certificadoFile}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.vPasswordCertificado && errors.vPasswordCertificado.message}
                    {(!errors.vPasswordCertificado && certificadoValidacion.mensaje) && certificadoValidacion.mensaje}
                </Form.Control.Feedback>
                <Form.Control.Feedback type="valid">
                    <FontAwesomeIcon icon="check-circle" className="me-1" />
                    Contraseña correcta - Certificado válido
                </Form.Control.Feedback>
                {validandoCertificado && (
                    <Form.Text className="text-info d-block mt-1">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Validando certificado...
                    </Form.Text>
                )}
                {!validandoCertificado && certificadoFile && !watch('vPasswordCertificado') && (
                    <Form.Text className="text-muted d-block mt-1">
                        Ingrese la contraseña para validar el certificado
                    </Form.Text>
                )}
            </Form.Group>

            {/* Información del Certificado */}
            {certificadoValidacion.validado && certificadoValidacion.esValido && certificadoValidacion.info && (
                <Alert variant="success" className="mb-3">
                    <Alert.Heading className="h6">
                        <FontAwesomeIcon icon="certificate" className="me-2" />
                        Información del Certificado
                    </Alert.Heading>
                    <hr />
                    <Row>
                        <Col md={6}>
                            <small className="text-muted">Nombre Común (CN)</small>
                            <div className="fw-semibold">{certificadoValidacion.info.commonName}</div>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted">Organización</small>
                            <div className="fw-semibold">{certificadoValidacion.info.organization}</div>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col md={6}>
                            <small className="text-muted">Válido desde</small>
                            <div className="fw-semibold">{certificadoValidacion.info.validFrom}</div>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted">Válido hasta</small>
                            <div className="fw-semibold">{certificadoValidacion.info.validTo}</div>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col md={12}>
                            <small className="text-muted">Emisor</small>
                            <div className="fw-semibold">{certificadoValidacion.info.issuer}</div>
                        </Col>
                    </Row>

                    {/* Advertencias */}
                    {certificadoValidacion.warnings && certificadoValidacion.warnings.length > 0 && (
                        <div className="mt-3">
                            {certificadoValidacion.warnings.map((warning, idx) => (
                                <div key={idx} className="text-warning">
                                    <small>{warning}</small>
                                </div>
                            ))}
                        </div>
                    )}
                </Alert>
            )}

            <hr className="my-3" />

            <h6 className="mb-3 fw-semibold small">
                <FontAwesomeIcon icon="key" className="me-2 text-primary" />
                Credenciales de Acceso DGII
            </h6>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">
                            Usuario
                            <Button
                                variant="link"
                                size="sm"
                                className="ms-2 p-0"
                                onClick={() => setValue('vUsuario', onGenerarCadena())}
                                type="button"
                            >
                                <FontAwesomeIcon icon="sync-alt" className="me-1" />
                                Generar
                            </Button>
                        </Form.Label>
                        <Form.Control
                            size="sm"
                            type="text"
                            isInvalid={!!errors.vUsuario}
                            {...register("vUsuario", {
                                required: 'El usuario es requerido',
                                maxLength: 40
                            })}
                            onKeyUp={(e) => { e.target.value = e.target.value.toUpperCase(); }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vUsuario && errors.vUsuario.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold small">
                            Contraseña
                            <Button
                                variant="link"
                                size="sm"
                                className="ms-2 p-0"
                                onClick={() => setValue('vPassword', onGenerarCadena())}
                                type="button"
                            >
                                <FontAwesomeIcon icon="sync-alt" className="me-1" />
                                Generar
                            </Button>
                        </Form.Label>
                        <Form.Control
                            size="sm"
                            type="text"
                            isInvalid={!!errors.vPassword}
                            {...register("vPassword", {
                                required: 'La contraseña es requerida',
                                maxLength: 40
                            })}
                            onKeyUp={(e) => { e.target.value = e.target.value.toUpperCase(); }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vPassword && errors.vPassword.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <div className="d-grid gap-2">
                <Button
                    type="submit"
                    variant="success"
                    size="sm"
                    disabled={guardando}
                >
                    {guardando ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Guardando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon="save" className="me-2" />
                            Guardar cambios
                        </>
                    )}
                </Button>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={onCancel}
                    disabled={guardando}
                >
                    <FontAwesomeIcon icon="times" className="me-2" />
                    Cancelar
                </Button>
            </div>
        </Form>
    );
};

TabEditar.propTypes = {
    rnc: PropTypes.object,
    cargando: PropTypes.bool,
    guardando: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onGenerarCadena: PropTypes.func.isRequired
};

export default TabEditar;
